// Segment-level geometry for polygon/line coordinates — pure, no DOM/Konva.
// The v3 coordinate format stores each segment's END anchor plus its control
// points, so a "segment" here pairs the previous anchor with one entry:
//
//   [x, y]                                    straight to (x,y)
//   [x, y, "curve",  cpX, cpY]                quadratic to (x,y)
//   [x, y, "bezier", c1X, c1Y, c2X, c2Y]      cubic to (x,y)
//   ["closing", ...]                          same, back to the start anchor
//
// Closed polygons without an explicit closing entry still render a straight
// closing edge (the SVG `Z`), so it is reported as an implicit segment.

const SAMPLES = 16;

export function pathSegments(coordinates = [], closed = false) {
  const startIdx = coordinates.findIndex((c) => Array.isArray(c) && typeof c[0] === 'number');
  if (startIdx < 0) return [];
  const start = [coordinates[startIdx][0], coordinates[startIdx][1]];
  const segs = [];
  let prev = start;
  let explicitClose = false;

  for (let i = startIdx + 1; i < coordinates.length; i += 1) {
    const c = coordinates[i];
    if (!Array.isArray(c)) continue;
    if (c[0] === 'closing') {
      explicitClose = true;
      if (c[1] === 'curve') segs.push({ index: i, kind: 'quad', from: prev, to: start, cps: [[c[2], c[3]]], closing: true });
      else if (c[1] === 'bezier') segs.push({ index: i, kind: 'cubic', from: prev, to: start, cps: [[c[2], c[3]], [c[4], c[5]]], closing: true });
      else segs.push({ index: i, kind: 'line', from: prev, to: start, cps: [], closing: true });
      continue;
    }
    const to = [c[0], c[1]];
    if (c[2] === 'curve') segs.push({ index: i, kind: 'quad', from: prev, to, cps: [[c[3], c[4]]] });
    else if (c[2] === 'bezier') segs.push({ index: i, kind: 'cubic', from: prev, to, cps: [[c[3], c[4]], [c[5], c[6]]] });
    else segs.push({ index: i, kind: 'line', from: prev, to, cps: [] });
    prev = to;
  }

  if (closed && !explicitClose && (prev[0] !== start[0] || prev[1] !== start[1])) {
    segs.push({ index: coordinates.length, kind: 'line', from: prev, to: start, cps: [], closing: true, implicit: true });
  }
  return segs;
}

export function pointOnSegment(seg, t) {
  const [x0, y0] = seg.from; const [x1, y1] = seg.to;
  if (seg.kind === 'line') return [x0 + (x1 - x0) * t, y0 + (y1 - y0) * t];
  const u = 1 - t;
  if (seg.kind === 'quad') {
    const [cx, cy] = seg.cps[0];
    return [u * u * x0 + 2 * u * t * cx + t * t * x1, u * u * y0 + 2 * u * t * cy + t * t * y1];
  }
  const [ax, ay] = seg.cps[0]; const [bx, by] = seg.cps[1];
  return [
    u * u * u * x0 + 3 * u * u * t * ax + 3 * u * t * t * bx + t * t * t * x1,
    u * u * u * y0 + 3 * u * u * t * ay + 3 * u * t * t * by + t * t * t * y1,
  ];
}

// Flattened polyline for one segment (excluding its start point).
export function sampleSegment(seg, steps = SAMPLES) {
  if (seg.kind === 'line') return [seg.to];
  const pts = [];
  for (let i = 1; i <= steps; i += 1) pts.push(pointOnSegment(seg, i / steps));
  return pts;
}

// The whole path flattened to points — curves included, so measurements and
// hit-tests follow the shape the user actually sees.
export function flattenPath(coordinates = [], closed = false, steps = SAMPLES) {
  const segs = pathSegments(coordinates, closed);
  if (!segs.length) {
    const start = coordinates.find((c) => Array.isArray(c) && typeof c[0] === 'number');
    return start ? [[start[0], start[1]]] : [];
  }
  const pts = [segs[0].from];
  for (const seg of segs) pts.push(...sampleSegment(seg, steps));
  return pts;
}

export function pathLength(coordinates = [], closed = false, steps = SAMPLES) {
  const pts = flattenPath(coordinates, closed, steps);
  let len = 0;
  for (let i = 1; i < pts.length; i += 1) len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  return len;
}

// Shoelace over the flattened outline — curved beds report their real area.
export function pathArea(coordinates = [], steps = SAMPLES) {
  const pts = flattenPath(coordinates, true, steps);
  if (pts.length < 3) return 0;
  let s = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const a = pts[i]; const b = pts[(i + 1) % pts.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2;
}

// Nearest segment to a point, with the parameter t where it is closest —
// sampled, so a click near a curve finds the curve rather than its chord.
export function nearestSegment(coordinates = [], closed = false, px, py, steps = SAMPLES) {
  const segs = pathSegments(coordinates, closed);
  let best = null;
  for (const seg of segs) {
    for (let i = 0; i <= steps; i += 1) {
      const t = i / steps;
      const [x, y] = pointOnSegment(seg, t);
      const d = Math.hypot(px - x, py - y);
      if (!best || d < best.distance) best = { seg, t, distance: d, point: [x, y] };
    }
  }
  return best;
}

const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

// Split a segment at t into two of the same kind (de Casteljau), so inserting a
// vertex on a curve keeps the curve's exact shape.
export function splitSegment(seg, t) {
  if (seg.kind === 'line') {
    const mid = lerp(seg.from, seg.to, t);
    return { point: mid, first: { kind: 'line', cps: [] }, second: { kind: 'line', cps: [] } };
  }
  if (seg.kind === 'quad') {
    const a = lerp(seg.from, seg.cps[0], t);
    const b = lerp(seg.cps[0], seg.to, t);
    const mid = lerp(a, b, t);
    return { point: mid, first: { kind: 'quad', cps: [a] }, second: { kind: 'quad', cps: [b] } };
  }
  const a = lerp(seg.from, seg.cps[0], t);
  const b = lerp(seg.cps[0], seg.cps[1], t);
  const c = lerp(seg.cps[1], seg.to, t);
  const d = lerp(a, b, t);
  const e = lerp(b, c, t);
  const mid = lerp(d, e, t);
  return { point: mid, first: { kind: 'cubic', cps: [a, d] }, second: { kind: 'cubic', cps: [e, c] } };
}

// Build a coordinate entry for a segment ending at `to` (or the closing entry).
export function segmentEntry(kind, cps, to, closing = false) {
  if (closing) {
    if (kind === 'quad') return ['closing', 'curve', cps[0][0], cps[0][1]];
    if (kind === 'cubic') return ['closing', 'bezier', cps[0][0], cps[0][1], cps[1][0], cps[1][1]];
    return ['closing'];
  }
  if (kind === 'quad') return [to[0], to[1], 'curve', cps[0][0], cps[0][1]];
  if (kind === 'cubic') return [to[0], to[1], 'bezier', cps[0][0], cps[0][1], cps[1][0], cps[1][1]];
  return [to[0], to[1]];
}

// Insert a vertex at the point on the path nearest (px,py). Curved segments are
// split into two curves of the same kind; straight ones just gain a point.
// Returns new coordinates, or null when nothing sensible can be inserted.
export function insertVertexNear(coordinates = [], closed = false, px, py) {
  const hit = nearestSegment(coordinates, closed, px, py);
  if (!hit) return null;
  const { seg, t } = hit;
  const clamped = Math.min(0.99, Math.max(0.01, t)); // never split exactly on an anchor
  const { point, first, second } = splitSegment(seg, clamped);
  const out = coordinates.slice();
  const firstEntry = segmentEntry(first.kind, first.cps, point, false);
  if (seg.implicit) {
    // The implicit closing edge of a polygon: the new point just becomes the
    // final anchor (the path still closes straight back to the start).
    out.push(firstEntry);
    return out;
  }
  const secondEntry = segmentEntry(second.kind, second.cps, seg.to, !!seg.closing);
  out.splice(seg.index, 1, firstEntry, secondEntry);
  return out;
}

// --- segment types (v1 naming) ---------------------------------------------
// The UI speaks v1's straight / curve / bezier; the segment model speaks
// line / quad / cubic.
export const SEGMENT_TYPES = ['straight', 'curve', 'bezier'];
const KIND_OF = { straight: 'line', curve: 'quad', bezier: 'cubic' };
const TYPE_OF = { line: 'straight', quad: 'curve', cubic: 'bezier' };
export const segmentType = (seg) => TYPE_OF[seg?.kind] || 'straight';

// v1's default controls for a segment turned into a curve (CurveSegment /
// BezierSegment.defaultControlPoint(s)): bowed out to the left of the
// direction of travel, 30% / 25% of the length.
export function defaultControls(type, from, to) {
  const dx = to[0] - from[0]; const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len; const ny = dx / len;
  if (type === 'curve') {
    const o = len * 0.3;
    return [[(from[0] + to[0]) / 2 + nx * o, (from[1] + to[1]) / 2 + ny * o]];
  }
  if (type === 'bezier') {
    const o = len * 0.25;
    return [
      [from[0] + dx / 3 + nx * o, from[1] + dy / 3 + ny * o],
      [from[0] + (2 * dx) / 3 + nx * o, from[1] + (2 * dy) / 3 + ny * o],
    ];
  }
  return [];
}

// A coordinate entry for a new `type` segment from `from` to `to`.
export function newSegmentEntry(type, from, to, closing = false) {
  return segmentEntry(KIND_OF[type] || 'line', defaultControls(type, from, to), to, closing);
}

// Change one segment (by its coordinate index — `seg.index`) to `type`.
// Returns new coordinates, or null when it already is that type.
export function setSegmentTypeAt(coordinates = [], closed = false, index, type) {
  const seg = pathSegments(coordinates, closed).find((s) => s.index === index);
  if (!seg || segmentType(seg) === type) return null;
  const out = coordinates.slice();
  out[index] = newSegmentEntry(type, seg.from, seg.to, !!seg.closing);
  return out;
}

// Split the segment at coordinate index `index` in the middle (the midpoint
// handles). Returns { coordinates, vertex } — `vertex` is the new anchor's
// coordinate index — or null.
export function insertVertexAt(coordinates = [], closed = false, index, t = 0.5) {
  const seg = pathSegments(coordinates, closed).find((s) => s.index === index);
  if (!seg) return null;
  const { point, first, second } = splitSegment(seg, t);
  const out = coordinates.slice();
  const firstEntry = segmentEntry(first.kind, first.cps, point, false);
  if (seg.implicit) { out.push(firstEntry); return { coordinates: out, vertex: out.length - 1 }; }
  out.splice(seg.index, 1, firstEntry, segmentEntry(second.kind, second.cps, seg.to, !!seg.closing));
  return { coordinates: out, vertex: seg.index };
}
